/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

/** The full-screen windows opened from the mobile top bar. */
export type MobileWindowId = "account" | "agenda" | "alerts";

interface MobileWindowState {
  /** The open window, if any: only one at a time. */
  active: MobileWindowId | null;
  setActive: (active: MobileWindowId | null) => void;
  /** Last scroll offset of the settings window, restored after a remount. */
  scrollTop: RefObject<number>;
}

const MobileWindowStateContext = createContext<MobileWindowState | null>(null);

/**
 * Holds which mobile top-bar window is open above react-admin's <Admin>.
 *
 * Changing the locale makes ra-core's I18nContextProvider re-key (so remount)
 * the whole app under it, MobileLayout included: state kept there is lost and
 * the settings window vanished as soon as a language was picked in it. Kept
 * up here, it survives that remount and the window comes back as it was.
 */
export const MobileWindowStateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [active, setActive] = useState<MobileWindowId | null>(null);
  const scrollTop = useRef(0);
  const value = useMemo(() => ({ active, setActive, scrollTop }), [active]);
  return (
    <MobileWindowStateContext.Provider value={value}>
      {children}
    </MobileWindowStateContext.Provider>
  );
};

/** The shared window state, or a local one when rendered without the provider. */
export const useMobileWindowState = (): MobileWindowState => {
  const shared = useContext(MobileWindowStateContext);
  const [active, setActive] = useState<MobileWindowId | null>(null);
  const scrollTop = useRef(0);
  return shared ?? { active, setActive, scrollTop };
};
